/**
 * Setup flow orchestrator.
 *
 * Reads `setupSteps` from OrgProvider and renders the first incomplete step.
 * Steps re-derive automatically as OrgProvider state changes (e.g. after
 * updateProfile / createOrg). When `setupSteps.length === 0`, the flow
 * returns null and unmounts naturally.
 *
 * Currently supported steps (in order):
 *  - `'profile'`          → <ProfileStep />
 *  - `'create-org'`       → <CreateOrgStep />
 *  - `'trial-activated'`  → <TrialActivatedStep /> (celebratory, post-create-org)
 *
 * Future extension: append a new step to the `SetupStep` union in
 * `lib/org-types.ts`, add derivation logic in `OrgProvider.setupSteps`,
 * and add a case here. The renderer simply maps the first unfinished step
 * to its component.
 *
 * @module components/setup/SetupFlow
 */

import React from 'react';
import { useOrg } from '../../lib/OrgProvider';
import { ProfileStep } from './ProfileStep';
import { CreateOrgStep } from './CreateOrgStep';
import { TrialActivatedStep } from './TrialActivatedStep';

/**
 * Renders the first incomplete setup step, or null when setup is complete.
 * Designed to be embedded inside a modal shell (see OnboardingSplash).
 */
export const SetupFlow: React.FC = () => {
  const { setupSteps } = useOrg();

  if (setupSteps.length === 0) {
    return null;
  }

  const current = setupSteps[0];

  switch (current) {
    case 'profile':
      return <ProfileStep />;
    case 'create-org':
      return <CreateOrgStep submitLabel="Get Started" />;
    case 'trial-activated':
      return <TrialActivatedStep />;
    default: {
      // Exhaustiveness check - if a new SetupStep is added, TS will flag this.
      const _exhaustive: never = current;
      return null;
    }
  }
};
